import { NatwestApplication } from "./NatwestApplication";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  natwestAdditionalDecisions,
  natwestApplicationStages,
  natwestStandardName,
  natwestAccountMatches,
  type NatwestAccountPlan as Model,
  type NatwestPlanFacts,
} from "@myfm/domain";
import { api } from "../api";
import { formatDateOnly } from "../dates";
import { isApiError } from "../api-error";
import { saveRequest } from "../ui/save-request";
import { RecordDraftBoundary, useRecordDraft } from "../ui/RecordDraftBoundary";
import { confirmDiscard } from "../ui/confirm-action";
import { ErrorState, Loading, reviewedApplicationError } from "../ui/State";
import { Link } from "../ui/ScopedLink";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { OnboardingReturnLink } from "../ui/OnboardingReturnLink";
type FollowUp = { ownerId: string; dueDate: string; note: string };
export function NatwestAccountPlan({ building }: { building: string }) {
  return (
    <RecordDraftBoundary subject="This bank account plan">
      <Workspace key={building} building={building} />
    </RecordDraftBoundary>
  );
}
function Workspace({ building }: { building: string }) {
  const root = `/api/building-setup/${building}/natwest-account-plan?building=${building}`;
  const [model, setModel] = useState<Model>(),
    [facts, setFacts] = useState<NatwestPlanFacts>(),
    [follow, setFollow] = useState<FollowUp | null>(null),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState("");
  const attempt = useRef<Record<string, unknown> | undefined>(undefined),
    lock = useRef(false);
  useRecordDraft(Boolean(facts) || busy || uncertain);
  async function load() {
    const value = await api<Model>(root);
    if (value.building !== building)
      throw reviewedApplicationError(
        "The bank account plan does not match this building.",
      );
    setModel(value);
    return value;
  }
  useEffect(() => {
    const reload = () => {
      if (!lock.current) void load().catch(setError);
    };
    reload();
    window.addEventListener("canonical-record-saved", reload);
    return () => window.removeEventListener("canonical-record-saved", reload);
  }, [root]);
  function edit(patch: Partial<NatwestPlanFacts>) {
    setFacts((f) => ({ ...f!, ...patch }));
    attempt.current = undefined;
    setNotice("");
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (lock.current || !facts || !model) return;
    lock.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const body = attempt.current ?? {
        ...facts,
        followUp: follow,
        expectedVersion: model.version,
        sourceHash: model.sourceHash,
        idempotencyKey: crypto.randomUUID(),
      };
      attempt.current = body;
      const result = await saveRequest<{ building: string; version: number }>(
        root,
        { method: "POST", body: JSON.stringify(body) },
      );
      if (
        result.building !== building ||
        result.version !== Number(body.expectedVersion) + 1
      )
        throw reviewedApplicationError(
          "The save was not confirmed. Retry the original request.",
        );
      await load();
      setFacts(undefined);
      setFollow(null);
      setUncertain(false);
      attempt.current = undefined;
      setNotice("Bank account plan saved. No application was sent.");
      window.dispatchEvent(new Event("canonical-record-saved"));
    } catch (e) {
      setError(e);
      const unknown =
        !isApiError(e) || e.statusCode === 0 || e.statusCode >= 500;
      setUncertain(unknown);
      if (!unknown) attempt.current = undefined;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  if (!model) return error ? <ErrorState error={error} /> : <Loading />;
  const view = facts ?? model.facts,
    disabled = busy || uncertain,
    open = model.actions.filter(
      (a) => !["Closed", "Verified", "Cancelled"].includes(a.status),
    );
  return (
    <section className="panel" aria-label="NatWest client money account plan">
      <OnboardingReturnLink building={building} />
      <h2>Client money accounts</h2>
      <p>{model.buildingName} · NatWest S42</p>
      <p>
        Both a service charge account and a reserves account are required. Check
        whether the building also needs a heat-network or other separate
        account.
      </p>
      <p>
        {model.ready
          ? "Required accounts are verified."
          : "Account setup is outstanding."}{" "}
        Finance handover is reviewed separately.
      </p>
      <p>
        <Link to={`/buildings/${building}/setup/financials?section=bank`}>
          Review existing bank accounts
        </Link>{" "}
        ·{" "}
        <Link to={`/buildings/${building}/setup/financials?section=handover`}>
          Review finance handover
        </Link>
      </p>
      {notice && <p role="status">{notice}</p>}
      {Boolean(error) && <ErrorState error={error} />}
      {!facts && (
        <>
          <p>
            Application: {model.facts.applicationStage} · Additional accounts:{" "}
            {model.facts.additionalDecision}
          </p>
          {model.facts.submissionDate && (
            <p>Submission date: {formatDateOnly(model.facts.submissionDate)}</p>
          )}
          {model.facts.bankReference && (
            <p>Bank reference: {model.facts.bankReference}</p>
          )}
          {model.facts.notes && <p>{model.facts.notes}</p>}
          {model.rows.map((r) => (
            <article className="queue-card" key={r.id}>
              <h3>{r.requestedName}</h3>
              <p>
                {r.purpose} ·{" "}
                {r.required
                  ? r.ready
                    ? "Verified account matched"
                    : "Account outstanding"
                  : "No longer required"}
              </p>
              <p>{r.description || "Standard building account"}</p>
              {r.bankAccountId && (
                <p>
                  Existing account:{" "}
                  {model.banks.find((b) => b.id === r.bankAccountId)?.name ??
                    "Account requires review"}
                </p>
              )}
              {r.missing.map((m) => (
                <p key={m}>{m}</p>
              ))}
              {!r.required && <p>{r.dispositionReason}</p>}
            </article>
          ))}
          {model.missing.length > 0 && (
            <details>
              <summary>What remains outstanding</summary>
              <ul>
                {model.missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </details>
          )}
          {model.canEdit && (
            <button
              onClick={() => {
                setFacts(structuredClone(model.facts));
                setError(undefined);
                setNotice("");
              }}
            >
              Edit account plan
            </button>
          )}
        </>
      )}
      {facts && (
        <form
          className="compact-form"
          onSubmit={save}
          aria-label="Bank account plan editor"
        >
          <fieldset disabled={disabled}>
            <legend>Account requirements</legend>
            <label>
              Does the client need additional accounts?
              <select
                value={view.additionalDecision}
                onChange={(e) =>
                  edit({
                    additionalDecision: e.target
                      .value as NatwestPlanFacts["additionalDecision"],
                  })
                }
              >
                {natwestAdditionalDecisions.map((d) => (
                  <option key={d}>{d}</option>
                ))}
              </select>
            </label>
            {facts.requirements.map((r, i) => {
              const standard =
                  r.purpose === "Service charge" || r.purpose === "Reserves",
                patch = (value: Partial<typeof r>) =>
                  edit({
                    requirements: facts.requirements.map((row, n) =>
                      n === i ? { ...row, ...value } : row,
                    ),
                  });
              const candidates = model.banks.filter((b) =>
                natwestAccountMatches(r.purpose, b),
              );
              return (
                <fieldset key={r.id}>
                  <legend>{r.purpose}</legend>
                  {standard ? (
                    <>
                      <p>
                        Requested account name:{" "}
                        <strong>{r.requestedName}</strong>
                      </p>
                      {r.requestedName !==
                        natwestStandardName(
                          model.buildingName,
                          r.purpose as "Service charge" | "Reserves",
                        ) && (
                        <button
                          type="button"
                          onClick={() =>
                            patch({
                              requestedName: natwestStandardName(
                                model.buildingName,
                                r.purpose as "Service charge" | "Reserves",
                              ),
                            })
                          }
                        >
                          Use current building name
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <label>
                        Account purpose
                        <select
                          disabled={
                            model.configured &&
                            model.facts.requirements.some(
                              (saved) => saved.id === r.id,
                            )
                          }
                          value={r.purpose}
                          onChange={(e) =>
                            patch({
                              purpose: e.target.value as typeof r.purpose,
                            })
                          }
                        >
                          {[
                            "Communal heat network",
                            "Major works",
                            "Other",
                          ].map((p) => (
                            <option key={p}>{p}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Purpose and reason
                        <textarea
                          required
                          value={r.description}
                          maxLength={300}
                          onChange={(e) =>
                            patch({ description: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Requested account name
                        <input
                          required
                          value={r.requestedName}
                          maxLength={120}
                          onChange={(e) =>
                            patch({ requestedName: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={r.required}
                          onChange={(e) =>
                            patch({ required: e.target.checked })
                          }
                        />
                        Account required
                      </label>
                      {!r.required && (
                        <label>
                          Reason no longer required
                          <textarea
                            required
                            value={r.dispositionReason}
                            onChange={(e) =>
                              patch({ dispositionReason: e.target.value })
                            }
                          />
                        </label>
                      )}
                    </>
                  )}
                  <label>
                    Match an existing account
                    <select
                      value={r.bankAccountId ?? ""}
                      onChange={(e) =>
                        patch({ bankAccountId: e.target.value || null })
                      }
                    >
                      <option value="">
                        Account still needed / not yet matched
                      </option>
                      {r.bankAccountId &&
                        !candidates.some((b) => b.id === r.bankAccountId) && (
                          <option value={r.bankAccountId}>
                            Retained account needs review
                          </option>
                        )}
                      {candidates.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name} · {b.type} · {b.verification}
                        </option>
                      ))}
                    </select>
                  </label>
                </fieldset>
              );
            })}
            <button
              type="button"
              className="secondary"
              disabled={facts.requirements.length >= 20}
              onClick={() =>
                edit({
                  additionalDecision: "Additional required",
                  requirements: [
                    ...facts.requirements,
                    {
                      id: crypto.randomUUID(),
                      purpose: "Communal heat network",
                      description: "",
                      requestedName: `${model.buildingName} Heat Network`,
                      required: true,
                      dispositionReason: "",
                      bankAccountId: null,
                    },
                  ],
                })
              }
            >
              Add additional account
            </button>
            <p>
              Keep full bank details and signatures in their restricted records.
            </p>
            <h3>Application tracking</h3>
            <p>
              Record any application already handled outside BlockGenie here.
              Use the NatWest application section below to prepare and approve a
              new submission.
            </p>
            <label>
              Application stage
              <select
                value={facts.applicationStage}
                onChange={(e) =>
                  edit({
                    applicationStage: e.target
                      .value as NatwestPlanFacts["applicationStage"],
                  })
                }
              >
                {natwestApplicationStages.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Actual submission date
              <DateOnlyInput
                value={facts.submissionDate ?? ""}
                onChange={(value) => edit({ submissionDate: value || null })}
              />
            </label>
            <label>
              NatWest reference
              <input
                value={facts.bankReference}
                maxLength={200}
                onChange={(e) => edit({ bankReference: e.target.value })}
              />
            </label>
            <label>
              Application notes
              <textarea
                value={facts.notes}
                maxLength={2000}
                onChange={(e) => edit({ notes: e.target.value })}
              />
            </label>
            {model.canTrack && (
              <>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(follow)}
                    onChange={(e) => {
                      attempt.current = undefined;
                      setFollow(
                        e.target.checked
                          ? {
                              ownerId: open[0]?.ownerId ?? "",
                              dueDate: open[0]?.dueDate ?? "",
                              note: open[0]?.note ?? "",
                            }
                          : null,
                      );
                    }}
                  />
                  {open.length
                    ? "Update the existing My Work follow-up"
                    : "Track the next action in My Work"}
                </label>
                {follow && (
                  <>
                    <label>
                      Follow-up owner
                      <select
                        aria-label="Follow-up owner"
                        required
                        value={follow.ownerId}
                        onChange={(e) => {
                          attempt.current = undefined;
                          setFollow({ ...follow, ownerId: e.target.value });
                        }}
                      >
                        <option value="">Choose an owner</option>
                        {model.owners.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Follow-up date
                      <DateOnlyInput
                        value={follow.dueDate}
                        onChange={(value) => {
                          attempt.current = undefined;
                          setFollow({ ...follow, dueDate: value });
                        }}
                      />
                    </label>
                    <label>
                      Next action
                      <textarea
                        required
                        minLength={3}
                        value={follow.note}
                        onChange={(e) => {
                          attempt.current = undefined;
                          setFollow({ ...follow, note: e.target.value });
                        }}
                      />
                    </label>
                  </>
                )}
              </>
            )}
            <button>{busy ? "Saving…" : "Save account plan"}</button>
          </fieldset>
          {uncertain && <button disabled={busy}>Confirm original save</button>}
          <button
            className="secondary"
            type="button"
            disabled={disabled}
            onClick={() =>
              void confirmDiscard("these unsaved account changes").then(
                (ok) => {
                  if (ok) {
                    setFacts(undefined);
                    setFollow(null);
                    attempt.current = undefined;
                    setError(undefined);
                  }
                },
              )
            }
          >
            Discard changes
          </button>
          <button
            className="secondary"
            type="button"
            disabled={disabled}
            onClick={() =>
              void load()
                .then(() =>
                  setNotice("Current accounts loaded; your draft is retained."),
                )
                .catch(setError)
            }
          >
            Refresh current accounts
          </button>
        </form>
      )}
      {model.actions.map((a) => (
        <p key={a.id}>
          <Link to={`/work/actions/${a.id}`}>Bank account follow-up</Link> ·{" "}
          {a.status} - {a.ownerName ?? "Owner awaited"} -{" "}
          {formatDateOnly(a.dueDate, "Date awaited")}
          <br />
          {a.note}
        </p>
      ))}
      {model.configured ? (
        <NatwestApplication building={building} />
      ) : (
        <p>Save the account requirements before preparing an application.</p>
      )}
    </section>
  );
}
